<template>
    <div class="c-inv" :style="wrapStyle">
        <div :style="innerStyle">
            <div :style="headerStyle">
                <div :style="eyebrowStyle">{{ agencyName }}</div>
                <h1 :style="h1Style">Invoices &amp; payments</h1>
                <p :style="leadStyle">
                    Tap each invoice to enter the paid date and amount.
                    Outstanding balances will be carried into the next period.
                </p>
            </div>

            <!-- ===== Active invoices (the editable list) ===== -->
            <CCard
                v-if="invoices.length"
                :extra-style="cardStyle"
            >
                <h3 :style="sectionTitleStyle">To record</h3>
                <div :style="invListStyle">
                    <div
                        v-for="(invoice, __index) in invoices"
                        :key="invoice.id"
                        :style="invRowStyle"
                    >
                        <div :style="invPeriodStyle">{{ getPeriodDate(invoice.period) }}</div>
                        <div :style="invMetaStyle">
                            <div :style="invMetaPrimaryStyle">
                                ${{ numberWithCommas(invoice.amount) }}
                                <span :style="invMetaDueStyle">due {{ invoice.due_date }}</span>
                            </div>
                            <div :style="invMetaSecondaryStyle">Period {{ getPeriodDate(invoice.period) }}</div>
                        </div>
                        <div :style="invInputColStyle">
                            <label :style="invFieldLabelStyle">Paid date</label>
                            <div class="form-field form-field--datepicker" ref="datepicker" :style="datepickerWrapStyle">
                                <input
                                    type="text"
                                    id="payment_received"
                                    :value="invoice.payment_received"
                                    name="payment_received"
                                    :style="datepickerInputStyle"
                                    placeholder="mm/dd/yyyy"
                                    @changeDate="(e) => onChangeDatePicker(e.target.value, invoice.id, __index)"
                                />
                                <button
                                    type="button"
                                    class="btn btn--icon"
                                    :style="datepickerBtnStyle"
                                >
                                    <i class="icon icon-calendar"></i>
                                </button>
                            </div>
                        </div>
                        <div :style="invInputColStyle">
                            <label :style="invFieldLabelStyle">Paid amount</label>
                            <div :style="currencyWrapStyle">
                                <CurrencyInput
                                    :value="Number(invoice.collected)"
                                    :options="currency_input_setting"
                                    :change="(value, _key) => updateInput(value, __index, _key)"
                                    :key_input="'collected'"
                                    :index="__index"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </CCard>

            <!-- ===== Pending invoices (read-only) ===== -->
            <CCard v-if="pending_invoices.length > 0" :extra-style="cardStyle">
                <h3 :style="sectionTitleStyle">Pending</h3>
                <p :style="sectionLeadStyle">
                    Already opened from earlier periods. Not editable here — TAG will reconcile.
                </p>
                <div :style="pendingListStyle">
                    <div
                        v-for="(item, idx) in pending_invoices"
                        :key="`pend-${idx}`"
                        :style="pendingRowStyle"
                    >
                        <div :style="pendingPeriodStyle">{{ getPeriodDate(item.period) }}</div>
                        <div :style="pendingAmountStyle">${{ numberWithCommas(item.amount) }}</div>
                        <div :style="pendingPaidStyle">
                            <span :style="pendingPaidLabelStyle">paid</span>
                            <span>${{ numberWithCommas(item.collected) }}</span>
                        </div>
                    </div>
                </div>
            </CCard>

            <!-- ===== Payments history (collapsed, read-only) ===== -->
            <CCard v-if="paid_invoices && paid_invoices.length" :extra-style="cardStyle">
                <h3 :style="sectionTitleStyle">History</h3>
                <p :style="sectionLeadStyle">
                    {{ paid_invoices.length }} invoice{{ paid_invoices.length === 1 ? '' : 's' }} completed and confirmed.
                </p>
                <div :style="historyListStyle">
                    <div
                        v-for="invoice in paid_invoices"
                        :key="`hist-${invoice.id || invoice.period}`"
                        :style="historyRowStyle"
                    >
                        <span :style="historyCheckStyle">✓</span>
                        <div :style="historyPeriodStyle">{{ getPeriodDate(invoice.period) }}</div>
                        <div :style="historyAmountStyle">${{ numberWithCommas(invoice.amount) }}</div>
                        <div :style="historyDateStyle">paid {{ invoice.payment_received }}</div>
                    </div>
                </div>
            </CCard>

            <!-- ===== Bottom nav ===== -->
            <div :style="navStyle">
                <CBtn ghost @click="onPrevPage">← Back to brands</CBtn>
                <span :style="navHintStyle">autosaved as you go</span>
                <CBtn primary big @click="onNextPage">Continue →</CBtn>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import { Datepicker } from "vanillajs-datepicker";
import CurrencyInput from "../components/CurrencyInput";
import { SECTIONS } from "../consts";
import ClipLoader from "vue-spinner/src/ClipLoader";
import { scrollToTop } from "../../../helpers/_scrollToTop";

import CCard from "../c-design/CCard.vue";
import CBtn from "../c-design/CBtn.vue";
import viewportMixin from "../c-design/useViewport";
import {
    C_BG,
    C_INK,
    C_INK2,
    C_MUTED,
    C_HAIR,
    C_CREAM,
    C_GREEN,
    C_PANEL,
    TAG_RED_DEEP,
    FONT_DISPLAY,
    FONT_BODY,
    navMobileStickyStyle,
} from "../c-design/tokens";

export default {
    name: "Wizard_Invoices",
    mixins: [viewportMixin],
    components: {
        CurrencyInput,
        ClipLoader,
        CCard,
        CBtn,
    },
    props: ["onChangeSection"],

    mounted() {
        this.getData();

        scrollToTop();
    },
    computed: {
        prevSection() {
            return this.$store.getters.getPrevComponent;
        },
        agencyName() {
            return this.$store.getters.getAgencyName;
        },
        isHaveSalePages() {
            return this.$store.getters.getAllSalesPages ? true : false
        },
        // ===== c-design style maps =====
        wrapStyle() {
            return {
                background: C_BG,
                minHeight: '70vh',
                padding: this.isMobile ? '14px 12px 110px' : '24px 16px 40px',
            };
        },
        innerStyle() {
            return { maxWidth: '900px', margin: '0 auto' };
        },
        headerStyle() {
            return {
                marginBottom: this.isMobile ? '16px' : '24px',
                padding: this.isMobile ? '0 4px' : '0 8px',
            };
        },
        eyebrowStyle() {
            return {
                fontSize: '12px',
                color: C_MUTED,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                fontWeight: 600,
            };
        },
        h1Style() {
            return {
                margin: '6px 0 8px',
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '26px' : '34px',
                fontWeight: 700,
                letterSpacing: this.isMobile ? '-0.7px' : '-1px',
                lineHeight: 1.1,
                color: C_INK,
            };
        },
        leadStyle() {
            return {
                margin: 0,
                fontSize: '15px',
                color: C_MUTED,
                lineHeight: 1.55,
                maxWidth: '600px',
            };
        },
        cardStyle() {
            return { marginBottom: '18px', padding: '24px 28px' };
        },
        sectionTitleStyle() {
            return {
                margin: '0 0 6px',
                fontFamily: FONT_DISPLAY,
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                color: C_MUTED,
            };
        },
        sectionLeadStyle() {
            return {
                margin: '0 0 16px',
                fontSize: '13px',
                color: C_INK2,
                lineHeight: 1.5,
            };
        },
        invListStyle() {
            return { display: 'flex', flexDirection: 'column', gap: '12px' };
        },
        invRowStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: '70px 1fr 180px 180px',
                gap: '14px',
                alignItems: 'center',
                padding: '14px 16px',
                background: C_CREAM,
                borderRadius: '14px',
            };
        },
        invPeriodStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: '14px',
                fontWeight: 700,
                color: C_INK,
                textAlign: 'center',
                padding: '8px',
                background: '#fff',
                borderRadius: '10px',
                border: `1px solid ${C_HAIR}`,
            };
        },
        invMetaStyle() {
            return { minWidth: 0 };
        },
        invMetaPrimaryStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: '18px',
                fontWeight: 700,
                color: C_INK,
                display: 'flex',
                alignItems: 'baseline',
                gap: '10px',
                flexWrap: 'wrap',
            };
        },
        invMetaDueStyle() {
            return {
                fontFamily: FONT_BODY,
                fontSize: '12px',
                fontWeight: 500,
                color: C_MUTED,
            };
        },
        invMetaSecondaryStyle() {
            return {
                fontSize: '12px',
                color: C_MUTED,
                marginTop: '2px',
            };
        },
        invInputColStyle() {
            return { display: 'flex', flexDirection: 'column', gap: '4px' };
        },
        invFieldLabelStyle() {
            return {
                fontSize: '11px',
                color: C_INK2,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
            };
        },
        datepickerWrapStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`,
                borderRadius: '10px',
                background: '#fff',
                padding: '0 8px 0 12px',
            };
        },
        datepickerInputStyle() {
            return {
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                padding: '10px 0',
                fontSize: '14px',
                fontFamily: FONT_BODY,
                color: C_INK,
                width: '100%',
            };
        },
        datepickerBtnStyle() {
            return {
                background: 'transparent',
                border: 'none',
                padding: '6px 4px',
                margin: '0 0 0 4px',
                fontSize: '16px',
                color: C_MUTED,
                cursor: 'pointer',
            };
        },
        currencyWrapStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`,
                borderRadius: '10px',
                background: '#fff',
                padding: '0 12px',
            };
        },
        pendingListStyle() {
            return { display: 'flex', flexDirection: 'column', gap: '8px' };
        },
        pendingRowStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: '90px 1fr auto',
                gap: '14px',
                alignItems: 'center',
                padding: '12px 16px',
                background: C_CREAM,
                borderRadius: '12px',
            };
        },
        pendingPeriodStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: '13px',
                fontWeight: 700,
                color: C_INK2,
            };
        },
        pendingAmountStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: '15px',
                fontWeight: 700,
                color: C_INK,
            };
        },
        pendingPaidStyle() {
            return {
                display: 'flex',
                alignItems: 'baseline',
                gap: '6px',
                fontFamily: FONT_DISPLAY,
                fontSize: '14px',
                fontWeight: 600,
                color: C_INK2,
            };
        },
        pendingPaidLabelStyle() {
            return {
                fontFamily: FONT_BODY,
                fontSize: '11px',
                fontWeight: 500,
                color: C_MUTED,
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
            };
        },
        historyListStyle() {
            return {
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                maxHeight: '280px',
                overflowY: 'auto',
                paddingRight: '4px',
            };
        },
        historyRowStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: '28px 90px 1fr auto',
                gap: '12px',
                alignItems: 'center',
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '13px',
            };
        },
        historyCheckStyle() {
            return {
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: C_GREEN,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '11px',
            };
        },
        historyPeriodStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: '13px',
                fontWeight: 700,
                color: C_INK2,
            };
        },
        historyAmountStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: '13px',
                fontWeight: 600,
                color: C_INK,
            };
        },
        historyDateStyle() {
            return {
                fontSize: '12px',
                color: C_MUTED,
            };
        },
        navStyle() {
            return navMobileStickyStyle(this.isMobile, {
                marginTop: '8px',
                padding: '16px 24px',
                background: '#fff',
                borderRadius: '16px',
                border: `1px solid ${C_HAIR}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
            });
        },
        navHintStyle() {
            return {
                fontSize: '12px',
                color: C_MUTED,
                fontStyle: 'italic',
            };
        },
    },
    data() {
        return {
            SECTIONS,
            invoices: [],
            pending_invoices: [],
            paid_invoices: [],
            currency_input_setting: {
                currency: "USD",
                hideCurrencySymbolOnFocus: false,
                hideGroupingSeparatorOnFocus: false,
                hideNegligibleDecimalDigitsOnFocus: false,
            },
            datepicker_options: {
                format: "mm/dd/yyyy",
                pickLevel: 0,
                autohide: true,
            },
            statuses: [
                {
                    id: 1,
                    name: "Unpaid",
                },
            ],
            filter: {
                status: [],
            },
            validation_rules: {},
            isInitPage: false,
            isLoading: false,
        };
    },

    methods: {
        getPeriodDate(date) {
            return date ? moment(date).format("MM/YYYY") : "";
        },
        async getData() {
            this.isLoading = true;

            await axios
                .post("/api/wizard/getList", {
                    page: "invoices_and_payments",
                })
                .then((res) => {
                    if (
                        typeof res.data.page !== "object" ||
                        res.data.page.length <= 0
                    ) {
                        this.onChangeSection(
                            SECTIONS.FINAL_DASHBOARD,
                            SECTIONS.EDIT_SALE_LINE_ITEM
                        );
                    } else {
                        this.invoices = res.data.page.invoices || [];
                        this.pending_invoices = res.data.page.pending_invoices || [];
                        this.paid_invoices = res.data.page.history;
                    }
                })
                .catch((err) =>
                    console.log("err getData invoices and payments", err)
                )
                .finally(() => {
                    this.initValidation();
                    this.initDatePicker();
                });
        },
        initDatePicker() {
            const node__datepicker = this.$refs.datepicker;

            if (!node__datepicker) {
                return;
            }

            const initDP = (__node) => {
                let datepicker = null;
                const input = __node.querySelector("input");
                const button = __node.querySelector("button");

                if (input) {
                    datepicker = new Datepicker(input, {
                        ...this.datepicker_options,
                    });
                }

                if (button) {
                    button.addEventListener("click", () => {
                        if (!datepicker.active) {
                            datepicker.show();
                        }
                    });
                }
            };

            if (Array.isArray(node__datepicker)) {
                node__datepicker.forEach((_datepicker) => initDP(_datepicker));
            } else {
                initDP(node__datepicker);
            }
        },

        onChangeDatePicker(value, invoice_id, index) {
            const date = value;

            if (date) {
                const invoice_index = this.invoices.findIndex(
                    (i) => i.id === invoice_id
                );

                if (invoice_index != -1) {
                    this.invoices[invoice_index].payment_received = date;
                }

                this.updateInput(date, index, "payment_received");
            }
        },
        onChangeStatusCheckbox(e) {
            if (e.target.checked) {
                this.filter.status.push(e.target.value);
            } else {
                this.filter.status = this.filters.status.filter(
                    (i) => i != e.target.value
                );
            }
        },
        onChangeStatusAll(e) {
            if (e.target.checked) {
                this.filter.status = [];
                this.statuses.forEach((filter) => {
                    const _checkbox =
                        this.$refs[`filter_status_${filter.id}`][0];
                    if (_checkbox.checked) {
                        _checkbox.checked = false;
                        _checkbox.dataset.checked = false;
                    }
                });
            }
        },
        numberWithCommas(x) {
            if (x === null || x === undefined || x === '') return '0';
            return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        },
        initValidation() {
            let __object_rules = {};

            let dataArray = Object.keys(this.invoices).map((key, index) => ({
                ...this.invoices[key],
            }));

            if (
                typeof this.invoices === "object" &&
                !Array.isArray(this.invoices)
            ) {
                dataArray = Object.keys(this.invoices).map((key, index) => ({
                    ...this.invoices[key],
                }));
            } else {
                dataArray = this.invoices;
            }

            dataArray.forEach((invoice) => {
                return (__object_rules[invoice.id] = {
                    collected: {
                        required: true,
                        value: invoice.collected,
                    },
                    payment_received: {
                        required: true,
                        value: invoice.payment_received,
                    },
                });
            });

            this.validation_rules = __object_rules;

            setTimeout(() => {
                this.isInitPage = true;
            }, 1000);

            this.isLoading = false;
        },
        checkValidation() {
            if (true) {
                let isValidate = true;

                let dataArray = Object.keys(this.invoices).map(
                    (key, index) => ({
                        ...this.invoices[key],
                    })
                );

                if (
                    typeof this.invoices === "object" &&
                    !Array.isArray(this.sales)
                ) {
                    dataArray = Object.keys(this.invoices).map(
                        (key, index) => ({
                            ...this.invoices[key],
                        })
                    );
                } else {
                    dataArray = this.invoices;
                }

                dataArray.forEach((invoice) => {
                    const _id = invoice.id;
                    const invoice_rules = this.validation_rules[_id];

                    if (
                        invoice_rules.collected.required &&
                        !invoice_rules.collected.value
                    ) {
                        isValidate = false;
                    } else if (
                        invoice_rules.payment_received.required &&
                        !invoice_rules.payment_received.value
                    ) {
                        isValidate = false;
                    }
                });

                return isValidate;
            }
        },
        updateValidationField(invoice_id, key, value) {
            this.validation_rules[invoice_id][key].value = value;
        },
        updateDataByField(key, value, id = 1) {
            axios
                .post("api/wizard/updateDraft", {
                    action: "updateMasterInvoice",
                    data: {
                        key,
                        value,
                        id,
                    },
                })
                .then((res) => {
                    //this.$toast.success(res.data.message);

                    this.updateValidationField(id, key, value);
                })
                .catch((err) => console.log("Err updateDataByField", err));
        },

        updateInput: _.debounce(function (value, index, key) {
            if (this.isInitPage) {
                this.updateDataByField(key, value, this.invoices[index].id);
            }
        }, 500),

        onNextPage() {
            let isValidate = this.checkValidation();

            if (isValidate) {
                this.onChangeSection(SECTIONS.FINAL_DASHBOARD);
            } else {
                this.$toast.error(
                    "You should fill in the amounts and dates for each row of the table"
                );
            }
        },
        onPrevPage() {
            if (this.isHaveSalePages) {
                this.onChangeSection(
                    SECTIONS.EDIT_SALE_LINE_ITEM,
                    SECTIONS.EDIT_SALE_LINE_ITEM
                )
            } else {
                this.onChangeSection(
                    SECTIONS.DASHBOARD,
                    SECTIONS.INVOICES
                )
            }
        }
    },
};
</script>
